<template>
  <div class="chatbot-composer">
    <input
      ref="fileInput"
      type="file"
      class="chatbot-composer__file-input"
      accept="image/png,image/jpeg,image/webp"
      @change="onFilesSelected"
    />

    <div class="composer-box">
      <v-btn
        v-if="Number(distanceToBottom) > 300"
        ref="scrollToBottomBtn"
        @click="onScrollToBottomClick"
        icon
        class="composer-box__prompt-btn"
      >
        <v-icon size="20">mdi-arrow-down</v-icon>
      </v-btn>
      <PromptTemplatesPanel ref="promptTemplates" :product="product" @apply-prompt-template="onApplyPromptTemplate" />

      <div v-if="attachments.length" class="composer-box__attachments">
        <div class="attachments-strip" role="list" aria-label="Uploaded attachments">
          <div v-for="att in visibleAttachments" :key="att.id" class="attachment" role="listitem">
            <button
              type="button"
              class="attachment__thumb"
              :aria-label="att.isVideo ? 'Preview video' : 'Preview image'"
              :title="att.isVideo ? 'Preview video' : 'Preview image'"
              :disabled="att.status !== 'ready'"
              @click="openPreview(att)"
            >
              <div v-if="att.isVideo" class="attachment__thumb--video">
                <v-icon class="attachment__thumb--video-icon" size="22" color="white">mdi-play</v-icon>
                <video class="attachment__img" :src="att.url" muted playsinline />
              </div>
              <img v-else class="attachment__img" :src="att.url" :alt="att.name" />
              <div v-if="att.status === 'uploading'" class="attachment__overlay" aria-label="Uploading">
                <v-progress-circular indeterminate size="18" width="2" color="white" />
              </div>
              <div
                v-else-if="att.status === 'error'"
                class="attachment__overlay attachment__overlay--error"
                aria-label="Upload failed"
              >
                <v-icon size="18" color="white">mdi-alert-circle</v-icon>
              </div>
            </button>

            <button
              type="button"
              class="attachment__remove"
              :aria-label="att.isVideo ? 'Remove video' : 'Remove image'"
              :title="att.isVideo ? 'Remove video' : 'Remove image'"
              @click="removeAttachment(att.id)"
            >
              <v-icon size="14">mdi-close</v-icon>
            </button>
          </div>

          <button
            v-if="hiddenCount > 0"
            type="button"
            class="attachments-more"
            :aria-label="`Show ${hiddenCount} more`"
            :title="`Show ${hiddenCount} more`"
            @click="showAllAttachments = true"
          >
            +{{ hiddenCount }}
          </button>
        </div>
      </div>

      <textarea
        ref="textarea"
        spellcheck="false"
        autocorrect="off"
        autocomplete="off"
        autocapitalize="off"
        v-model="localText"
        class="composer-box__input"
        :placeholder="placeholder"
        rows="1"
        @keydown="onKeydown"
        @paste="onPaste"
      />

      <div class="composer-box__toolbar">
        <div class="composer-box__toolbar-left">
          <div class="composer-plus-menu" v-click-outside="closeComposerPlusMenu">
            <button
              type="button"
              class="composer-btn composer-btn--plus"
              :disabled="disabled"
              aria-label="Add attachment or template"
              aria-haspopup="true"
              :aria-expanded="showComposerPlusMenu ? 'true' : 'false'"
              @click="toggleComposerPlusMenu"
            >
              <v-icon size="22" color="black">mdi-plus</v-icon>
            </button>

            <button
              type="button"
              class="composer-btn composer-btn--plus"
              :disabled="disabled"
              aria-label="Upload image"
              title="Upload image"
              aria-haspopup="true"
              :aria-expanded="showComposerPlusMenu ? 'true' : 'false'"
              @click="onMenuUploadImages"
            >
              <v-icon size="18" color="black">mdi-paperclip</v-icon>
            </button>

            <button
              type="button"
              class="composer-btn composer-btn--plus"
              :disabled="disabled"
              aria-label="Add image from URL"
              title="Add image from URL"
              aria-haspopup="true"
              :aria-expanded="showComposerPlusMenu ? 'true' : 'false'"
              @click="onMenuAddMediaFromUrl"
            >
              <v-icon size="18" color="black">mdi-link-variant</v-icon>
            </button>

            <transition name="menu-fade">
              <div v-if="showComposerPlusMenu" class="composer-plus-menu__dropdown" role="menu">
                <div class="composer-plus-menu__dropdown-content">
                  <div class="composer-plus-menu__dropdown-title">Create new ads image</div>
                  <button type="button" class="model-menu__item" role="menuitem" @click="onMenuEditImage">
                    <v-icon size="18">mdi-palette</v-icon>
                    Edit existing image
                  </button>
                  <button type="button" class="model-menu__item" role="menuitem" @click="onMenuRedesignImage">
                    <v-icon size="18">mdi-palette</v-icon>
                    Redesign image
                  </button>
                </div>
                <button type="button" class="model-menu__item" role="menuitem" @click="onMenuSelectTemplate">
                  <v-icon size="18">mdi-auto-fix</v-icon>
                  Prompt library
                </button>
              </div>
            </transition>
          </div>
        </div>

        <div class="composer-box__toolbar-right">
          <div class="model-selector" v-click-outside="closeModelMenu">
            <button type="button" class="composer-btn composer-btn--model" @click="toggleModelMenu">
              {{ activeModelLabel }}
              <v-icon size="18" class="model-selector__arrow">mdi-chevron-down</v-icon>
            </button>

            <transition name="menu-fade">
              <div v-if="showModelMenu" class="model-menu">
                <div v-for="provider in providers" :key="provider.id" class="model-menu__group">
                  <div class="model-menu__provider">{{ provider.name }}</div>
                  <button
                    v-for="model in provider.models"
                    :key="model.id"
                    type="button"
                    class="model-menu__item"
                    :class="{ 'model-menu__item--active': model.id === selectedModelId }"
                    @click="selectModel(provider, model)"
                  >
                    {{ model.name }}
                    <v-icon v-if="model.id === selectedModelId" size="16" color="primary" class="ml-auto">
                      mdi-check
                    </v-icon>
                  </button>
                </div>
              </div>
            </transition>
          </div>

          <button
            v-if="showPauseButton"
            type="button"
            class="composer-btn composer-btn--pause"
            aria-label="Pause"
            title="Pause"
            @click="onPauseSendMessage"
            :disabled="loadingPauseSendMessage"
          >
            <span class="composer-btn--pause-inner"></span>
          </button>

          <button
            v-else
            type="button"
            class="composer-btn composer-btn--send"
            :class="{ 'composer-btn--send-active': canSend }"
            :disabled="!canSend"
            aria-label="Send"
            title="Send"
            @click="submit"
          >
            <v-icon size="20">mdi-arrow-up</v-icon>
          </button>
        </div>
      </div>
    </div>

    <PreviewImage
      v-if="preview.src && !preview.isVideo"
      :src="preview.src"
      :show-preview="preview.show"
      @close="closePreview"
    />
    <PreviewVideo
      v-if="preview.src && preview.isVideo"
      :src="preview.src"
      :show-preview="preview.show"
      @close="closePreview"
    />
    <PromptTemplatesLibrary
      v-if="promptTemplatesLibraryOpen"
      :product="product"
      :show-library="promptTemplatesLibraryOpen"
      @close="onClosePromptTemplatesLibrary"
      @submit="onSubmitFromLibrary"
    />

    <AddMediaFromUrlDialog
      v-model="showAddMediaUrlModal"
      media-types="image"
      :max-attachments="maxAttachments"
      :attachments-count="attachments.length"
      @save="onSaveMediaFromUrlDialog"
    />

    <ImageGeneration
      v-model="showImageGenerationModal"
      :mode="imageGenerationMode"
      :concept="imageGenerationConcept"
      @submit="onSubmitFromImageGeneration"
      :product="product"
    />
  </div>
</template>

<script>
import { AI_PROVIDERS, DEFAULT_PROVIDER_ID, DEFAULT_MODEL_ID } from './utils/ai-providers';
import PromptTemplatesPanel from './PromptTemplatesPanel.vue';
import PreviewImage from './PreviewImage.vue';
import PreviewVideo from './PreviewVideo.vue';
import attachmentUploadMixin from './utils/attachment-upload-mixin';
import PromptTemplatesLibrary from './PromptTemplatesLibrary.vue';
import AddMediaFromUrlDialog from './AddMediaFromUrlDialog.vue';
import ImageGeneration from './ImageGeneration.vue';
import { filenameFromMediaUrl } from './utils';

export default {
  name: 'ChatbotComposer',
  components: {
    PromptTemplatesPanel,
    PreviewImage,
    PreviewVideo,
    PromptTemplatesLibrary,
    AddMediaFromUrlDialog,
    ImageGeneration,
  },
  mixins: [attachmentUploadMixin],

  props: {
    value: { type: String, default: '' },
    product: { type: Object, default: () => ({}) },
    placeholder: { type: String, default: 'Ask anything' },
    disabled: { type: Boolean, default: false },
    showPauseButton: { type: Boolean, default: false },
    providerId: { type: String, default: DEFAULT_PROVIDER_ID },
    modelId: { type: String, default: DEFAULT_MODEL_ID },
    loadingPauseSendMessage: { type: Boolean, default: false },
  },

  data() {
    return {
      localText: this.value,
      preview: {
        show: false,
        src: '',
        isVideo: false,
      },
      showModelMenu: false,
      providers: AI_PROVIDERS,
      selectedProviderId: this.providerId,
      selectedModelId: this.modelId,
      promptTemplatesLibraryOpen: false,
      showComposerPlusMenu: false,
      promptType: 'normal_chat', // 'normal_chat' | 'template'
      /** Pending image API mode from Edit/Redesign modal; cleared after send or on API success. */
      composerMode: null,
      distanceToBottom: 0,
      showAddMediaUrlModal: false,
      showImageGenerationModal: false,
      imageGenerationMode: 'edit',
      imageGenerationConcept: null,
    };
  },

  computed: {
    canSend() {
      return (
        !this.disabled &&
        !this.isUploadingAttachments &&
        (this.localText.trim().length > 0 || this.attachments.length > 0)
      );
    },
    activeModelLabel() {
      const provider = this.providers.find(p => p.id === this.selectedProviderId);
      if (!provider) return 'Select model';
      const model = provider.models.find(m => m.id === this.selectedModelId);
      return model?.name ?? '';
    },
  },

  watch: {
    value(v) {
      if (v !== this.localText) {
        this.localText = v;
        this.scheduleAutoResize();
      }
    },
    localText(v) {
      this.$emit('input', v);
      this.scheduleAutoResize();
    },
    showImageGenerationModal(open) {
      if (!open) this.imageGenerationConcept = null;
    },
  },

  mounted() {
    this.autoResize();
    this.$nextTick(() => {
      this.attachListScrollListeners();
    });
  },

  beforeDestroy() {
    if (this._autoResizeRaf != null) {
      cancelAnimationFrame(this._autoResizeRaf);
      this._autoResizeRaf = null;
    }

    this.detachListScrollListeners();
  },

  methods: {
    /** Scrollable message list in ChatbotContainer — not global querySelector (wrong instance / timing). */
    getListScrollEl() {
      const root = this.$el && typeof this.$el.closest === 'function' ? this.$el.closest('.chatbot-container') : null;
      return (
        root?.querySelector('.chatbot-container-list-wrapper') ??
        document.querySelector('.chatbot-container-list-wrapper')
      );
    },
    attachListScrollListeners() {
      const listWrapper = this.getListScrollEl();
      if (!listWrapper || listWrapper === this._listScrollEl) return;
      this.detachListScrollListeners();
      this._listScrollEl = listWrapper;
      listWrapper.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.updateScrollToBottomHint);
      this.updateScrollToBottomHint();
    },
    detachListScrollListeners() {
      if (this._listScrollEl) {
        this._listScrollEl.removeEventListener('scroll', this.handleScroll);
        this._listScrollEl = null;
      }
      window.removeEventListener('resize', this.updateScrollToBottomHint);
    },
    handleScroll() {
      this.updateScrollToBottomHint();
    },
    updateScrollToBottomHint() {
      const el = this._listScrollEl;
      if (!el) return;
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      this.distanceToBottom = Math.round(dist);
    },
    onScrollToBottomClick() {
      this.$emit('scroll-to-bottom');
    },
    onApplyPromptTemplate(pt) {
      this.$emit('apply-prompt-template', pt && pt.prompt ? String(pt.prompt) : '');
      this.$nextTick(() => {
        this.scheduleAutoResize();
        if (this.$refs.textarea) this.$refs.textarea.focus();
      });
    },
    onKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.submit();
      }
    },

    /** Coalesces resize to one layout read per frame; avoids duplicate @input + watcher reflows. */
    scheduleAutoResize() {
      if (this._autoResizeRaf != null) return;
      this._autoResizeRaf = requestAnimationFrame(() => {
        this._autoResizeRaf = null;
        this.autoResize();
      });
    },

    autoResize() {
      const el = this.$refs.textarea;
      if (!el) return;
      el.style.height = 'auto';
      const scrollH = el.scrollHeight;
      const maxH = 180;
      el.style.height = Math.min(scrollH, maxH) + 'px';
      el.style.overflowY = scrollH > maxH ? 'auto' : 'hidden';
    },

    /** Called by parent after a message is sent successfully so the user can type the next one. */
    focusTextarea() {
      this.$nextTick(() => {
        const el = this.$refs.textarea;
        if (el && typeof el.focus === 'function') el.focus();
      });
    },

    onPaste(e) {
      if (this.disabled) return;

      const items = e?.clipboardData?.items ? Array.from(e.clipboardData.items) : [];
      const mediaItems = items.filter(i => {
        if (!i || i.kind !== 'file') return false;
        const file = typeof i.getAsFile === 'function' ? i.getAsFile() : null;
        return file && this.isAcceptedFileType(file);
      });
      if (mediaItems.length === 0) return;

      const remainingSlots = Math.max(0, this.maxAttachments - this.attachments.length);
      if (remainingSlots <= 0) {
        this.notifyError(
          `You can only upload up to ${this.maxAttachments} ${
            this.maxAttachments > 1 ? 'images' : 'image'
          } per message.`,
        );
        return;
      }

      const accepted = mediaItems.slice(0, remainingSlots);
      accepted.forEach(it => {
        const file = typeof it.getAsFile === 'function' ? it.getAsFile() : null;
        if (file) this.addAttachment(file);
      });

      if (mediaItems.length > remainingSlots) {
        this.notifyError(
          `Only ${remainingSlots} more ${remainingSlots > 1 ? 'images' : 'image'} can be added to this message.`,
        );
      }

      if (accepted.length > 0) this.showAllAttachments = false;
    },

    removeAttachment(id) {
      const att = this.attachments.find(a => a.id === id);
      if (att && this.preview.src && (att.url === this.preview.src || att.remoteUrl === this.preview.src)) {
        this.closePreview();
      }
      attachmentUploadMixin.methods.removeAttachment.call(this, id);
    },

    openPreview(att) {
      if (!att?.url || att.status !== 'ready') return;
      this.preview.src = att.url;
      this.preview.isVideo = !!att.isVideo;
      this.preview.show = true;
    },

    closePreview() {
      this.preview.show = false;
      this.preview.src = '';
      this.preview.isVideo = false;
    },

    selectModel(provider, model) {
      this.selectedProviderId = provider.id;
      this.selectedModelId = model.id;
      this.showModelMenu = false;
      this.$emit('model-change', {
        providerId: provider.id,
        modelId: model.id,
      });
    },

    closeModelMenu() {
      this.showModelMenu = false;
    },

    toggleModelMenu() {
      this.showModelMenu = !this.showModelMenu;
      if (this.showModelMenu) this.showComposerPlusMenu = false;
    },

    toggleComposerPlusMenu() {
      if (this.disabled) return;
      this.showComposerPlusMenu = !this.showComposerPlusMenu;
      if (this.showComposerPlusMenu) this.showModelMenu = false;
    },

    closeComposerPlusMenu() {
      this.showComposerPlusMenu = false;
    },

    onMenuUploadImages() {
      if (this.attachments.length >= this.maxAttachments) {
        this.notifyError(
          `You can only attach up to ${this.maxAttachments} ${
            this.maxAttachments > 1 ? 'images' : 'image'
          } per message.`,
        );
        return;
      }
      this.closeComposerPlusMenu();
      this.$nextTick(() => {
        if (this.$refs.fileInput) this.$refs.fileInput.click();
      });
    },

    onMenuAddMediaFromUrl() {
      if (this.attachments.length >= this.maxAttachments) {
        this.notifyError(
          `You can only attach up to ${this.maxAttachments} ${
            this.maxAttachments > 1 ? 'images' : 'image'
          } per message.`,
        );
        return;
      }
      this.closeComposerPlusMenu();
      this.showAddMediaUrlModal = true;
    },

    onSaveMediaFromUrlDialog(payload) {
      const items =
        Array.isArray(payload?.items) && payload.items.length > 0
          ? payload.items.map(it => ({
              url: it?.url != null ? String(it.url).trim() : '',
              isVideo: !!it?.isVideo,
            }))
          : Array.isArray(payload?.urls)
          ? payload.urls.map(u => ({ url: String(u || '').trim(), isVideo: false }))
          : [];
      const ready = items.filter(it => it.url);
      if (!ready.length) {
        this.notifyError('Add at least one URL that loads successfully.');
        return;
      }
      const slots = Math.max(0, this.maxAttachments - this.attachments.length);
      if (slots <= 0) {
        this.notifyError(`You can only attach up to ${this.maxAttachments} files per message.`);
        return;
      }
      const toAdd = ready.slice(0, slots);
      toAdd.forEach(({ url, isVideo }) => {
        this.attachments.push({
          id: this.objectId(),
          file: null,
          name: filenameFromMediaUrl(url, isVideo),
          url,
          localUrl: '',
          remoteUrl: url,
          status: 'ready',
          isVideo,
        });
      });
      if (ready.length > slots) {
        this.notifyError(`Only ${slots} more attachment(s) could be added.`);
      }
      this.showAllAttachments = false;
    },

    onMenuSelectTemplate() {
      this.closeComposerPlusMenu();
      this.onOpenPromptTemplatesLibrary();
    },

    onMenuEditImage() {
      this.closeComposerPlusMenu();
      this.imageGenerationConcept = null;
      this.imageGenerationMode = 'edit';
      this.showImageGenerationModal = true;
    },

    onMenuRedesignImage() {
      this.closeComposerPlusMenu();
      this.imageGenerationConcept = null;
      this.imageGenerationMode = 'redesign';
      this.showImageGenerationModal = true;
    },

    openImageGenerationWithConcept(concept) {
      let normalized = null;

      if (typeof concept === 'string') {
        const text = concept.trim();
        if (!text) return;
        normalized = { title: 'Prompt', description: text };
      } else if (concept && typeof concept === 'object') {
        const title = concept?.title || 'Prompt';
        const description = concept?.description || '';
        if (!title && !description) return;
        normalized = { title, description };
      } else {
        return;
      }

      this.imageGenerationConcept = normalized;
      this.imageGenerationMode = 'concept';
      this.showImageGenerationModal = true;
    },

    onSubmitFromImageGeneration({ message, attachments = [], image_ref = [], mode }) {
      if (this.disabled) return;

      const text = String(message || '').trim();
      if (!text) return;

      const normalizeFile = file => ({
        id: file.id,
        name: file.name,
        src: file.remoteUrl || file.url || file.src,
        status: file.status || 'ready',
      });

      const getValidFiles = files =>
        files
          .filter(file => file && file.status !== 'error' && (file.remoteUrl || file.url || file.src))
          .map(normalizeFile);

      this.showImageGenerationModal = false;
      this.imageGenerationConcept = null;
      this.closePreview();

      this.$emit('send', {
        text,
        files: [],
        attachments: getValidFiles(attachments),
        image_ref: getValidFiles(image_ref),
        providerId: this.selectedProviderId,
        modelId: this.selectedModelId,
        promptType: this.promptType,
        mode,
      });
    },

    submit() {
      if (!this.canSend) return;
      this.closeComposerPlusMenu();
      if (this.$refs.promptTemplates && typeof this.$refs.promptTemplates.collapse === 'function') {
        this.$refs.promptTemplates.collapse();
      }
      const text = this.localText.trim();
      const payload = {
        text,
        files: this.attachments.map(a => a.file),
        attachments: this.attachments.map(a => ({
          id: a.id,
          name: a.name,
          src: a.remoteUrl || a.url,
          status: a.status,
        })),
        providerId: this.selectedProviderId,
        modelId: this.selectedModelId,
        promptType: this.promptType,
        mode: this.composerMode,
      };
      this.attachments.forEach(a => {
        if (a?.localUrl) URL.revokeObjectURL(a.localUrl);
        if (a?.url && String(a.url).startsWith('blob:')) URL.revokeObjectURL(a.url);
      });
      this.localText = '';
      this.attachments = [];
      this.showAllAttachments = false;
      this.closePreview();
      this.scheduleAutoResize();
      this.$emit('send', payload);
      // Failed messages keep promptType/mode on the local message for retry; composer resets for new input.
      this.promptType = 'normal_chat';
      this.composerMode = null;
    },

    clearComposerMode() {
      this.composerMode = null;
    },
    onOpenPromptTemplatesLibrary() {
      this.promptTemplatesLibraryOpen = true;
    },
    onClosePromptTemplatesLibrary() {
      this.promptTemplatesLibraryOpen = false;
    },
    onSubmitFromLibrary({ message, attachments }) {
      if (this.disabled) return;

      const text = message != null ? String(message).trim() : '';
      const list = Array.isArray(attachments) ? attachments : [];
      const ready = list.filter(a => a && a.status !== 'error' && (a.remoteUrl || a.url || a.src));
      if (!text && !ready.length) return;

      this.promptTemplatesLibraryOpen = false;
      this.closePreview();

      this.$emit('send', {
        text,
        files: [],
        attachments: ready.map(a => ({
          id: a.id,
          name: a.name,
          src: a.remoteUrl || a.url || a.src,
          status: a.status || 'ready',
        })),
        providerId: this.selectedProviderId,
        modelId: this.selectedModelId,
        promptType: 'template',
        mode: null,
      });
    },
    onPauseSendMessage() {
      this.$emit('pause-send-message');
    },
  },
};
</script>

<style lang="scss" scoped>
.chatbot-composer {
  flex: 0 0 auto;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  border-radius: 24px;
  padding: 0 8px 16px;

  @media (max-width: 768px) {
    padding: 0 8px 16px;
  }
}

.chatbot-composer__file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.composer-box {
  background: #fff;
  border: 1.5px solid var(--v-border-base);
  border-radius: 24px;
  padding: 4px 4px 0;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.08);
  position: relative;

  &:hover {
    box-shadow: 0 0 11px 0 rgba(0, 0, 0, 0.18);
  }
}

/* File chips above textarea */
.composer-box__attachments {
  padding: 10px 12px 0;
}

.attachments-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 2px;
  padding-top: 6px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }
}

.attachment {
  position: relative;
  flex: 0 0 auto;
}

.attachment__thumb {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f6f6f6;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  display: grid;
  place-items: center;

  &:disabled {
    cursor: default;
    opacity: 0.85;
  }
}

.attachment__thumb--video {
  position: relative;
  display: grid;
  place-items: center;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1;
  }
  &-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }
}

.attachment__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.attachment__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
}

.attachment__overlay--error {
  background: rgba(211, 47, 47, 0.65);
}

.attachment__remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  z-index: 2;
}

.attachments-more {
  flex: 0 0 auto;
  height: 64px;
  width: 64px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px dashed rgba(0, 0, 0, 0.18);
  background: rgba(0, 0, 0, 0.03);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.3s ease;

  &:hover {
    background: var(--main-background);
  }
}

/* Plain textarea */
.composer-box__input {
  display: block;
  width: 100%;
  min-height: 24px;
  max-height: 180px;
  padding: 12px 16px 4px;
  border: none;
  outline: none;
  resize: none;
  font-size: 15px;
  line-height: 1.5;
  font-family: inherit;
  color: var(--main-text-color);
  background: transparent;
  overflow-y: hidden;

  &::placeholder {
    color: #9e9e9e;
    font-weight: 400;
  }
}

/* Bottom toolbar */
.composer-box__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 6px;
}

.composer-box__toolbar-left {
  display: flex;
  align-items: center;
}

/* "+" menu: attach / template */
.composer-plus-menu {
  position: relative;
}

.composer-btn--plus {
  width: 36px;
  height: 36px;
  border-radius: 50%;

  &:hover:not(:disabled) {
    background: #f0f0f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.composer-plus-menu__dropdown {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  min-width: 240px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  padding: 6px 0;
  z-index: 100;
}

.composer-box__toolbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Generic composer button reset */
.composer-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 50%;
  padding: 0;
  transition: background 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

/* Model selector button */
.composer-btn--model {
  display: inline-flex;
  align-items: center;
  height: 36px;
  gap: 2px;
  border-radius: 16px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background: #f0f0f0;
  }
}

.model-selector__arrow {
  margin-left: 2px;
}

/* Send button */
.composer-btn--send {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #fff;
  background: #d6d6d6;
  cursor: default;
  transition: background 0.2s ease;
}

.composer-btn--pause {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #fff;
  background: var(--v-primary-base);
  cursor: pointer;

  .composer-btn--pause-inner {
    height: 12px;
    width: 12px;
    display: block;
    background: #fff;
    border-radius: 2px;
  }
}

.composer-btn--send-active {
  background: var(--v-primary-base);
  cursor: pointer;

  &:hover {
    background: var(--v-primary-base);
  }

  i {
    color: #fff !important;
  }
}

/* Model dropdown menu */
.model-selector {
  position: relative;
}

.model-menu {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  padding: 6px 0;
  z-index: 100;
}

.model-menu__group + .model-menu__group {
  border-top: 1px solid #f0f0f0;
}

.model-menu__provider {
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.45);
}

.composer-plus-menu__dropdown-content {
  border-bottom: 1px solid #f0f0f0;
}

.composer-plus-menu__dropdown-title {
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.45);
}

.model-menu__item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: none;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  gap: 8px;

  &:hover {
    background: #f5f5f5;
  }
}

.model-menu__item--active {
  color: var(--v-primary-base, #1976d2);
  font-weight: 500;
}

/* Dropdown animation */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.menu-fade-enter,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.composer-box__prompt-btn {
  position: absolute;
  top: -55px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  height: 36px !important;
  width: 36px !important;
  border: 1px solid var(--v-border-base) !important;
  background: #ffffff !important;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.12));
  z-index: 2;
  animation: prompt-btn-slide-in 0.2s ease-in-out forwards;
}

@keyframes prompt-btn-slide-in {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(-100%);
  }
}
</style>
